import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { OrganizationsNavigationProvider } from '../../../organizations/providers/navigation-provider';
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { GuestFormComponent } from '../../components/form/form';
import { BreadcrumbProvider, Crumb } from '../../../breadcrumb/providers/breadcrumb-provider';
import { GuestsAthletesFormComponent } from '../../components/form/athletes';
import { GuestsTeamsFormComponent } from '../../components/form/teams';
import { GuestsProvider, GuestRow } from '../../providers/guests-provider';
import { GuestRights } from '../../providers/guests-rights';
import { UsersProvider } from '../../../users/providers/users-provider';
import { forkJoin, Observable, of, Subscription, switchMap, takeUntil, throwError } from 'rxjs';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ModuleMenu } from "../../../common/models/menu";
import { Organization } from "../../../common/models/organization";
import { Athlete } from "../../../common/models/athlete";
import { Team } from "../../../common/models/team";
import { SimpleMatDataSource } from "../../../common/providers/simple-mat-data-source";
import { ConfigProvider } from "../../../config-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AthletesProvider } from "../../../athletes/providers/athletes-provider";
import { TeamsProvider } from "../../../teams/providers/teams-provider";
import { InfoDialogComponent } from "../../../common/components/info-dialog/info-dialog";
import { ConfirmDialogComponent } from "../../../common/components/confirm-dialog/confirm-dialog";
import { catchError, map } from "rxjs/operators";
import { User } from "../../../common/models/user";
import { Guest } from "../../../common/models/guest";
import { HeaderComponent } from "../../../common/components/header-elitment/header-elitment";
import { PageContentComponent } from "../../../common/components/page-content/page-content";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { MatIcon } from "@angular/material/icon";
import { MatFormField } from "@angular/material/form-field";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell, MatHeaderCellDef,
  MatHeaderRow, MatHeaderRowDef,
  MatRow, MatRowDef,
  MatTable
} from "@angular/material/table";
import { NgIf, NgOptimizedImage } from "@angular/common";
import { MatIconButton } from "@angular/material/button";
import { MatInput } from "@angular/material/input";
import { MatTooltip } from "@angular/material/tooltip";
import { error } from "@angular/compiler-cli/src/transformers/util";
import { CoachRow } from "../../../coaches/providers/coaches-provider";
import { OrganizationRole } from "../../../common/models/organization-role";
import { ApiPaginator } from "../../../common/components/api-paginator/api-paginator";
import { CenterLoaderComponent } from "../../../common/components/center-loader/center-loader";
import { Paginator } from "../../../common/models/paginator";

@Component({
  selector: 'guests-list-page',
  providers: [],
  styleUrls: ['./list.scss'],
  standalone: true,
  imports: [
    HeaderComponent,
    MatPaginator,
    PageContentComponent,
    ModuleContentComponent,
    Error404Component,
    Error401Component,
    MatIcon,
    MatFormField,
    MatMenu,
    MatCell,
    MatHeaderCell,
    MatTable,
    MatRow,
    MatHeaderRow,
    MatSortModule,
    MatMenuTrigger,
    MatColumnDef,
    MatCellDef,
    MatMenuItem,
    MatIconButton,
    MatHeaderRowDef,
    NgIf,
    MatRowDef,
    MatInput,
    MatHeaderCellDef,
    MatTooltip,
    TranslateModule,
    NgOptimizedImage,
    ApiPaginator,
    CenterLoaderComponent,
  ],
  templateUrl: './list.html'
})
export class GuestsListPageComponent implements AfterViewInit, OnDestroy {

    // public DESCRIPTION_MAX_LENGTH: number = 5000;

    public pageTitle = 'Guests';
    public mainActions: ModuleMenu[] = [];
    public isLoading = true;
    public is401 = false;
    public is404 = false;

    public activePage: number = 1;
    public activeOrderBy: string = 'name_asc';
    private userFilter = '';

    public organization: Organization | null = null;
    public availableAthletes: Athlete[] = [];
    public availableTeams: Team[] = [];
    public guestsPaginator: Paginator = {} as Paginator;

    public rights: GuestRights = new GuestRights(this._utils);
    public dataSource: SimpleMatDataSource = new SimpleMatDataSource([]);
    public columns: string[] = ['avatar', 'name', 'athletes', 'teams', 'actions'];

    @ViewChild(MatPaginator, {static: false}) public paginator: MatPaginator | undefined;
    @ViewChild(MatSort, {static: false}) public sorter: MatSort | undefined;

    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        public breadcrumbProvider: BreadcrumbProvider,
        public currentUser: CurrentUser,
        private _utils: UtilsProvider,
        private _title: Title,
        private _organizationNavigation: OrganizationsNavigationProvider,
        private _router: Router,
        private _dialoger: MatDialog,
        private _toaster: MatSnackBar,
        private _currentUserRoles: CurrentUserRolesProvider,
        private _route: ActivatedRoute,
        private _translater: TranslateService,
        private _dialog: MatDialog,
        private _guestsProvider: GuestsProvider,
        private _athletesProvider: AthletesProvider,
        private _teamsProvider: TeamsProvider,
        private _usersProvider: UsersProvider
    ) {
    }

    public ngAfterViewInit() {
      this._route.params.pipe(
        takeUntil(this.subscriptions),
        switchMap(() => this.loadOrganization()),
        catchError((error: any) => {
          this.onError(error);
          return of(null);
        }),
      ).subscribe({
        next: () => {
          this.initPage();
        },
        error: (error: any) => this.onError(error)
      });
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    // PUBLIC METHODS

    public onDisplayGuestClicked(toView: GuestRow) {
        // this._router.navigateByUrl('/' + this.organization!.code + '/coaches/' + toView.coach.id);
        console.error('Pas implémenté');
    }

    public onAddGuestClicked(): void {
        const dialogRef: any = this._dialog.open(GuestFormComponent, {
            data: {
                organization: this.organization,
                formTitle: 'Guest_creation'
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: GuestRow) => {
                if (row) {
                  this.setGuestsRows({}).subscribe();
                }
            })
        );
    }

    public onModifyGuestClicked(toModify: GuestRow): void {
        const dialogRef: any = this._dialog.open(GuestFormComponent, {
            data: {
                organization: this.organization,
                guestRow: toModify,
                formTitle: 'Guest_edition'
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: GuestRow) => {
                if (row) {
                    for (let i = 0; i < this.dataSource.count; i++) {
                        if (this.dataSource.get(i).guest.id === row.guest.id) {
                            this.dataSource.set(i, row);
                            return;
                        }
                    }
                }
            })
        );
    }

    public onManageAthletesClicked(toModify: GuestRow): void {

        const dialogRef: any = this._dialog.open(GuestsAthletesFormComponent, {
            data: {
                organization: this.organization,
                guestRow: toModify,
                availableAthletes: this.availableAthletes,
                formTitle: 'Guest_Athlete_edition',
                confirmButtonTitle: this._translater.instant('Save')
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: GuestRow) => {
                if (row) {
                    for (let i = 0; i < this.dataSource.count; i++) {
                        if (this.dataSource.get(i).guest.id === row.guest.id) {
                            this.dataSource.set(i, row);
                            return;
                        }
                    }
                }
            })
        );
    }

    public onManageTeamsClicked(toModify: GuestRow): void {

        const dialogRef: any = this._dialog.open(GuestsTeamsFormComponent, {
            data: {
                organization: this.organization,
                availableTeams: this.availableTeams,
                guestRow: toModify,
                formTitle: 'GuestTeam_edition',
                confirmButtonTitle: this._translater.instant('Save')
            }
        });

        // màj de la liste
        this.subscriptions.push(
            dialogRef.afterClosed().subscribe((row: GuestRow) => {
                if (row) {
                    for (let i = 0; i < this.dataSource.count; i++) {
                        if (this.dataSource.get(i).guest.id === row.guest.id) {
                            this.dataSource.set(i, row);
                            return;
                        }
                    }
                }
            })
        );
    }

    public onEnableGuestClicked(toEnable: GuestRow, active: boolean): void {
      this.isLoading = true;
      this.enableGuest(toEnable.guest, active).subscribe({
        next: (guest) => {
          if (guest) {
            toEnable.user.active = +active;
          }
          this.isLoading = false;
          this._toaster.open(this._translater.instant(active ? 'User_enabled' : 'User_disabled'), '', {duration: 5000});
        },
        error: (error) => {
          this.isLoading = false;
          this.onError(error);
        },
      });
    }

    public onDeleteGuestClicked(toDelete: GuestRow): void {

        if (toDelete.athletes.length > 0 || toDelete.teams.length > 0) {

            const dialog: MatDialogRef<any> = this._dialoger.open(InfoDialogComponent, {
                data: {
                    text: this._translater.instant('Quest_relations_no_delete')
                }
            });
        } else {

            const dialog: MatDialogRef<any> = this._dialoger.open(ConfirmDialogComponent, {
                data: {
                    confirmText: this._translater.instant('Confirm_delete_guest')
                }
            });

            this.subscriptions.push(
                dialog.afterClosed().subscribe((confirmed: boolean) => {
                    if (confirmed) {
                        this.isLoading = true;
                        this.deleteGuest(toDelete.guest).subscribe({
                          next: () => {
                            this.isLoading = false;
                            this._toaster.open(this._translater.instant('Guest_deleted'), '', {duration: 5000});
                          },
                          error: () => {
                            this.isLoading = false;
                            this._toaster.open(this._translater.instant('Guest_delete_error'), '', {duration: 5000});
                          }
                        });
                    }
                })
            );
        }
    }

    public onSortChange(event: any): void {
      if (event.active === 'name') {
        this.setGuestsRows({orderBy: `name_${event.direction}`}).subscribe();
      }
      this.dataSource.sortBy(event.active, event.direction);
    }

    public onFilterKeyup(filter: string): void {
      this.setGuestsRows({guestName: filter}).subscribe();
    }

    // PRIVATE METHODS

    private loadOrganization(): Observable<any> {
      return this._organizationNavigation.getOrganisationFromRoute().pipe(
        map((organization: Organization | null) => {
          if (organization) {
            this.organization = organization;
          } else {
            throw {error: 'Organization not found', status: 404};
          }
        }),
      );
    }

    private initPage(): void {
      this.loadData().subscribe({
        next: () => {
          this.initNav();
          this.initSorters();
          this.initSearch();
          this.dataSource.sortBy('name', 'asc');
          this.isLoading = false;
        },
        error: (error) => this.onError(error)
      });
    }

    private initNav(): void {

        if (!this.rights.LIST) {
            this.is401 = true;
            return;
        }

        if (this.rights.SEND_INVITATION) {
            this.columns = ['avatar', 'name', 'athletes', 'teams', 'notvalidate', 'actions'];
        }

        this.dataSource.paginator = this.paginator!;

        if (this.rights.ADD) {
            this.mainActions = [
                {title: 'Add', icon: 'add', url: null, onClick: () => this.onAddGuestClicked()}
            ];
        }

        this.createPageTitle();
        this.createBreadcrumb();
    }

    public onResendInvitationClicked(user: User) {
        this.isLoading = true;
        this._usersProvider.sendInvitation(user).subscribe({
            next: () => {
                this.isLoading = false;
                this._toaster.open(this._translater.instant('Invitation_sent'), '', {duration: 5000});
            },
            error: () => {
                this.isLoading = false;
                this._toaster.open(this._translater.instant('Invitation_send_error'), '', {duration: 5000});
            }
        });
    }

    private initSorters(): void {
        this.dataSource.addSortDefinition(
            'name',
            (a: GuestRow, b: GuestRow) => this._utils.sortRow((`${a.user.firstName} ${a.user.lastName}`).toLowerCase(), (`${b.user.firstName} ${b.user.lastName}`).toLowerCase()),
            (a: GuestRow, b: GuestRow) => this._utils.sortRow((`${b.user.firstName} ${b.user.lastName}`).toLowerCase(), (`${a.user.firstName} ${a.user.lastName}`).toLowerCase())
        );
    }

    private initSearch(): void {
        this.dataSource.sort = this.sorter!;
        this.dataSource.filterPredicate = (data: GuestRow, filter: string) => {
            const str: string = this._utils.normalizeString(`${data.user.firstName} ${data.user.lastName}`);
            return str.includes(filter);
        };
    }

    private loadData(): Observable<any> {
      if (!this.organization) {
        return throwError(() => this.onError({error: 'Organization not found', status: 404}));
      }
      return forkJoin([
        this.setGuestsRows({}),
        this._athletesProvider.getAthletes(this.organization.id!),
        this._teamsProvider.getTeams({organizationId: this.organization.id!})
      ]).pipe(
        map(([guests, athletes, teams] : [void, User[], Team[]]) => {
          this.availableAthletes = athletes;
          this.availableTeams = teams;

          this.rights.evaluateScreenRights({
              rolesIds: this._currentUserRoles.getRolesIds(this.organization?.id!)
          });
        })
      );
    }

    public pageChange(page: number): void {
      this.isLoading = true;
      this.setGuestsRows({page: page}).subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: (error) => {
          this.onError(error);
        },
      });
    }


  private setGuestsRows({orderBy = this.activeOrderBy, page = this.activePage, guestName = this.userFilter}: {orderBy?: string, page?: number, guestName?: string}): Observable<void> {
    return this._guestsProvider.getGuestsPaginator({
      organizationId: this.organization?.id!, 
      orderBy: orderBy, 
      pageNumber: page,
      userName: guestName
    }).pipe(
      map((guestsPaginator: Paginator) => {
        this.activePage = page;
        this.activeOrderBy = orderBy;
        this.guestsPaginator = guestsPaginator;
        this.userFilter = guestName;

        let guests: GuestRow[] = [];
        guestsPaginator.data.forEach((user: User) => {
          guests.push(this._guestsProvider.createGuestRow(this.organization!.id!, user));
        });
        this.guestsPaginator.data = guests;
        this.dataSource.replaceAllWith(guests);

        this.dataSource.paginator = this.paginator!;
      })
    );
  }

    private enableGuest(guest: Guest, active: boolean): Observable<User|null> {
      if (!guest.userId) {
        this.onError({error: 'User ID not found', status: 404});
        return of(null);
      }
      return this._usersProvider.updateUser(guest.userId, {'active': active});
    }

    private deleteGuest(guest: Guest): Observable<any> {
      if (!guest.userId) {
        this.onError({error: 'User ID not found', status: 404});
        return of();
      }
      return this._guestsProvider.deleteGuest(this.organization?.id!, guest.userId).pipe(
        map(() => {
          for (let i = 0; i < this.dataSource.count; i++) {
            if (this.dataSource.get(i).guest.id === guest.id) {
              this.dataSource.remove(i);
              i = this.dataSource.count;
            }
          }
        }),
      );
    }

    private createPageTitle() {
        this.pageTitle = this._translater.instant('Organization_guests');
        this._title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }

    private createBreadcrumb() {

        const crumbs: Crumb[] = [
            {
                shortTitle: 'Guests',
                title: 'Guests',
                url: this._router.url
            }
        ];
        this.breadcrumbProvider.setDefaultCrumbs(crumbs);
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 404) {
            this.is404 = true;
        } else if (error.status === 401) {
            this.is401 = true;
        }
    }
}
